'use client';

import classnames from 'classnames';
import { QA_MATCH_CENTRE_AROUND_THE_GROUNDS } from '@test-config/testIds';
import { AblyComponent } from '@stadionhq-v3/ably';
import { AroundTheGroundsInlineGrouped } from '@stadionhq-v3/football-ui';

import dayjs from '@lib/dayjs';
import { stripTrailingSlash } from '@stadionhq-v3/utilities';
import templateConfig from '@templateConfig';
import {
  aroundTheGroundsAblyMutator,
  AroundTheGroundsAblyResponse,
  AroundTheGroundsLiveProps,
  toAroundTheGroundsCompetition,
  toAroundTheGroundsRows,
} from '@utils/transforms/components/live/aroundTheGrounds.transform';
import styles from './matchCentreAroundTheGrounds.module.scss';
import { MatchCentreAroundTheGroundsProps } from './matchCentreAroundTheGrounds.types';

const MatchCentreAroundTheGrounds = ({
  testId = QA_MATCH_CENTRE_AROUND_THE_GROUNDS.COMPONENT,
  classNames = [],
  nativeProps = {},
  dictionary,
  SPORTS_DATA_API,
  fixtureCompetition,
  clientTeam,
  locale,
  fixtureData,
  CONTENTFUL_ENVIRONMENT,
  fallbackClubCrest,
  ...ablyProps
}: MatchCentreAroundTheGroundsProps) => {
  const classes = classnames([styles.matchCentreAroundTheGrounds], classNames);

  const djs = dayjs(locale);
  // Local-timezone match day — used for the REST feed query.
  const date = djs(fixtureData.kickOffUtc)
    .tz(djs.tz.guess())
    .format('YYYY-MM-DD');
  // UTC match day — the Ably channel keys off the fixture's UTC kickoff date
  // (backend formats `KickOffUtc:yyyyMMdd`), which can differ from the local
  // date near a midnight boundary.
  const channelDate = djs.utc(fixtureData.kickOffUtc).format('YYYYMMDD');

  // Must match the backend's channel exactly:
  // `live-scores|{competitionId}|{yyyyMMdd}|{locale}|{environment}`
  // (see platform-ably MessageExtensions.GetChannelName). The locale segment was
  // previously missing, so the client subscribed to a channel nobody published to.
  const aroundTheGroundsAblyChannelName: string | undefined =
    fixtureCompetition.id
      ? `live-scores|${fixtureCompetition.id}|${channelDate}|${SPORTS_DATA_API.LOCALE || locale}|${CONTENTFUL_ENVIRONMENT}`
      : undefined;

  const getLiveScoresFeedUrl = () => {
    const { LIVE_SCORES } = templateConfig.SPORTS_DATA_API.ENDPOINTS;

    if (!SPORTS_DATA_API.BASE_URL) {
      return '';
    }

    const stageIdParam = fixtureData.stageId
      ? `&stageId=${fixtureData.stageId}`
      : '';

    return `${stripTrailingSlash(SPORTS_DATA_API.BASE_URL)}/${stripTrailingSlash(LIVE_SCORES || '')}?teamId=${clientTeam?.dataProviderId}&competitionId=${fixtureCompetition.id}&date=${date}&locale=${SPORTS_DATA_API.LOCALE || locale}${stageIdParam}`;
  };

  return ablyProps ? (
    <AblyComponent<AroundTheGroundsLiveProps, AroundTheGroundsAblyResponse>
      {...ablyProps}
      channelName={aroundTheGroundsAblyChannelName}
      fetchInitialStateFromEndpoint={{
        url: getLiveScoresFeedUrl(),
        useMutator: true,
      }}
      storeKey="Around the Grounds"
      mutator={aroundTheGroundsAblyMutator(locale)}
      render={({ rowsById, competition }: AroundTheGroundsLiveProps) => (
        <AroundTheGroundsInlineGrouped
          {...nativeProps}
          rows={toAroundTheGroundsRows(rowsById, fallbackClubCrest)}
          competition={toAroundTheGroundsCompetition(competition)}
          testId={testId}
          classNames={[classes]}
          buttonText={dictionary.buttonText}
          title={dictionary.title}
          labels={{
            halfTimeAbbreviated: dictionary.halfTimeAbbreviated,
            fullTimeAbbreviated: dictionary.fullTimeAbbreviated,
            postponedAbbreviated: dictionary.postponedAbbreviated,
            abandonedAbbreviated: dictionary.abandonedAbbreviated,
            kickOffAbbreviated: dictionary.kickOffAbbreviated,
            versusAbbreviated: dictionary.versusAbbreviated,
            preMatch: dictionary.preMatch,
            live: dictionary.live,
            postMatch: dictionary.postMatch,
            noMatches: dictionary.noMatches,
          }}
        />
      )}
    />
  ) : (
    <></>
  );
};

export default MatchCentreAroundTheGrounds;
